/**
 * Script Blocker für Cookie Consent
 * Blockiert Scripts bis zur Einwilligung und aktiviert sie danach
 */
import { pmLog, pmWarn, pmError } from './Logger.js';

export class ScriptBlocker {
    constructor() {
        // Standard-Kategorien
        this.consent = {
            necessary: true,    // Immer aktiv
            analytics: false,
            marketing: false,
            external: false
        };

        // Service-spezifische Consents
        this.serviceConsent = {};

        // Bereits aktivierte Kategorien tracken
        this.activatedCategories = new Set(['necessary']);

        // Bereits aktivierte Services tracken
        this.activatedServices = new Set();

        pmLog('ScriptBlocker initialized');
    }

    /**
     * Consent laden und gespeicherte Kategorien aktivieren
     */
    loadConsent(cookieData) {
        if (cookieData && cookieData.categories) {
            this.consent = { ...this.consent, ...cookieData.categories };
            pmLog('Loaded consent:', this.consent);

            // Service-spezifische Consents laden
            if (cookieData.services) {
                this.serviceConsent = { ...cookieData.services };
                pmLog('Loaded service consent:', this.serviceConsent);
            }

            // Update GTM Consent Mode (if enabled)
            this.updateGTMConsent();

            // Publish state for third-party tools (independent of GTM)
            this._publishConsentState();

            // Aktiviere bereits akzeptierte Services
            Object.keys(this.serviceConsent).forEach(service => {
                if (this.serviceConsent[service] && !this.activatedServices.has(service)) {
                    this.activateService(service);
                }
            });

            // Aktiviere bereits akzeptierte Kategorien
            Object.keys(this.consent).forEach(category => {
                if (this.consent[category] && !this.activatedCategories.has(category)) {
                    this.activateCategory(category);
                }
            });
        }
    }

    /**
     * Einzelnes Script aktivieren
     */
    activateScript(oldScript) {
        try {
            // Neues Script-Element erstellen
            const newScript = document.createElement('script');
            newScript.type = 'text/javascript';

            // Externe Quelle kopieren
            if (oldScript.dataset.src) {
                newScript.src = oldScript.dataset.src;
                pmLog('  → Loading external script:', newScript.src);
            }

            // Inline Code kopieren
            if (oldScript.textContent && oldScript.textContent.trim()) {
                try {
                    // Validate that content doesn't start with HTML tags
                    const content = oldScript.textContent.trim();
                    if (content.startsWith('<') && !content.startsWith('<!--')) {
                        pmError('  → Skipping inline script with invalid content (starts with HTML tag)');
                        return;
                    }

                    newScript.textContent = oldScript.textContent;
                    pmLog('  → Loading inline script');
                } catch (contentError) {
                    pmError('  → Error copying script content:', contentError);
                    return;
                }
            }

            // Attribute kopieren (außer type, data-src, data-consent, data-consent-service)
            Array.from(oldScript.attributes).forEach(attr => {
                if (!['type', 'data-src', 'data-consent', 'data-consent-service'].includes(attr.name)) {
                    try {
                        newScript.setAttribute(attr.name, attr.value);
                    } catch (attrError) {
                        pmWarn('  → Could not copy attribute:', attr.name, attrError);
                    }
                }
            });

            // Altes Script durch neues ersetzen
            if (!oldScript.isConnected || !oldScript.parentNode) {
                pmError('  → Script is detached from DOM, cannot activate');
                return;
            }
            try {
                oldScript.parentNode.replaceChild(newScript, oldScript);
            } catch (replaceError) {
                // Fallback für Race Conditions (z.B. Elementor verschiebt DOM-Knoten
                // zwischen querySelector und setTimeout-Callback)
                try {
                    oldScript.insertAdjacentElement('afterend', newScript);
                    oldScript.remove();
                } catch (fallbackError) {
                    pmError('  → Script activation failed (both strategies):', fallbackError);
                }
            }
        } catch (error) {
            pmError('Error activating script:', error, oldScript);
            // Log the script content for debugging
            if (oldScript.textContent) {
                pmError('Script content preview:', oldScript.textContent.substring(0, 200));
            }
        }
    }

    /**
     * Konvertiert youtube.com zu youtube-nocookie.com wenn useNoCookie aktiviert ist.
     * Prüft window.pixelmateConfig.activeServices.youtube.useNoCookie
     */
    maybeConvertToNoCookieUrl(url, service) {
        if (service !== 'youtube') return url;
        const useNoCookie = window.pixelmateConfig?.activeServices?.youtube?.useNoCookie;
        if (!useNoCookie) return url;
        if (!url || url.includes('youtube-nocookie.com')) return url;
        return url
            .replace(/https?:\/\/(www\.)?youtube\.com\//g, 'https://www.youtube-nocookie.com/')
            .replace(/https?:\/\/youtu\.be\//g, 'https://www.youtube-nocookie.com/embed/');
    }

    /**
     * Alle Scripts einer Kategorie aktivieren (nur nicht-service-spezifische)
     */
    activateCategory(category) {
        // Prüfen ob bereits aktiviert
        if (this.activatedCategories.has(category)) {
            pmLog(`Category "${category}" already activated`);
            return;
        }

        pmLog(`Activating category: ${category}`);

        // Finde alle blockierten Scripts dieser Kategorie (ohne service-spezifische)
        const selector = `script[type="text/plain"][data-consent="${category}"]:not([data-consent-service])`;
        const blockedScripts = document.querySelectorAll(selector);

        pmLog(`Found ${blockedScripts.length} blocked script(s) for category`);

        // Aktiviere jedes Script
        blockedScripts.forEach((script, index) => {
            // Kleine Verzögerung zwischen Scripts für Abhängigkeiten
            setTimeout(() => {
                this.activateScript(script);
            }, index * 50);
        });

        // Aktiviere auch iframes (ohne service-spezifische)
        this.activateIframes(category);

        // Aktiviere blockierte <link>-Tags (z.B. Google-Fonts-Stylesheets) dieser
        // Kategorie. Anders als bei Scripts schließen wir service-spezifische NICHT
        // aus: Fonts sind render-relevant und sollen zurückkommen, sobald der Zweck
        // (Kategorie) akzeptiert wurde – unabhängig von granularem Service-Consent.
        this.activateLinks(category);

        // Aktiviere blockierte <img>-Tags (z.B. VG-WORT-Zählmarken) dieser Kategorie
        this.activateImagesForCategory(category);

        // Kategorie als aktiviert markieren
        this.activatedCategories.add(category);
        pmLog(`Category "${category}" activated`);
    }

    /**
     * Service-spezifische Scripts und iframes aktivieren
     */
    activateService(service) {
        // Prüfen ob bereits aktiviert
        if (this.activatedServices.has(service)) {
            pmLog(`Service "${service}" already activated`);
            return;
        }

        pmLog(`Activating service: ${service}`);

        // Finde alle blockierten Scripts dieses Services
        const scriptSelector = `script[type="text/plain"][data-consent-service="${service}"]`;
        const blockedScripts = document.querySelectorAll(scriptSelector);

        pmLog(`Found ${blockedScripts.length} blocked script(s) for service`);

        // Aktiviere jedes Script
        blockedScripts.forEach((script, index) => {
            setTimeout(() => {
                this.activateScript(script);
            }, index * 50);
        });

        // Aktiviere service-spezifische iframes (data-consent-service Attribut, JS-blockiert)
        const iframeSelector = `iframe[data-consent-service="${service}"][data-src]`;
        const blockedIframes = document.querySelectorAll(iframeSelector);

        pmLog(`Found ${blockedIframes.length} JS-blocked iframe(s) for service`);

        blockedIframes.forEach(iframe => {
            if (iframe.dataset.src) {
                iframe.src = this.maybeConvertToNoCookieUrl(iframe.dataset.src, service);
                iframe.removeAttribute('data-src');
                iframe.removeAttribute('sandbox');
                pmLog('  → Activated JS-blocked service iframe:', iframe.src);
            }
        });

        // Aktiviere PHP-seitig blockierte iframes (data-service + data-consent="external")
        // PHP blockiert Iframes mit data-consent="external" und data-service="<serviceName>",
        // NICHT mit data-consent-service. Daher brauchen wir einen zusätzlichen Selector.
        const phpIframeSelector = `iframe[data-service="${service}"][data-consent="external"][data-src]:not([data-consent-service])`;
        const phpBlockedIframes = document.querySelectorAll(phpIframeSelector);

        pmLog(`Found ${phpBlockedIframes.length} PHP-blocked iframe(s) for service`);

        phpBlockedIframes.forEach(iframe => {
            if (iframe.dataset.src) {
                iframe.src = this.maybeConvertToNoCookieUrl(iframe.dataset.src, service);
                iframe.removeAttribute('data-src');
                iframe.removeAttribute('sandbox');
                // Display wiederherstellen falls versteckt
                if (iframe.style.display === 'none') {
                    iframe.style.display = '';
                }
                pmLog('  → Activated PHP-blocked service iframe:', iframe.src);
            }
        });

        // Aktiviere blockierte <link>-Tags dieses Services (z.B. Google Fonts)
        this.activateLinkService(service);

        // Aktiviere blockierte <img>-Tags dieses Services (z.B. VG-WORT-Zählmarke)
        this.activateImagesForService(service);

        // Service als aktiviert markieren
        this.activatedServices.add(service);
        pmLog(`Service "${service}" activated`);
    }

    /**
     * Stellt blockierte <link>-Tags einer Kategorie wieder her.
     * PHP (ServiceInjector) ersetzt href durch data-pixelmate-href, damit der
     * Browser die Ressource (z.B. Google-Fonts-Stylesheet) ohne Consent nicht lädt.
     */
    activateLinks(category) {
        const selector = `link[data-pixelmate-href][data-consent="${category}"]`;
        document.querySelectorAll(selector).forEach(link => this.activateLink(link));
    }

    /**
     * Stellt blockierte <link>-Tags eines Services wieder her.
     */
    activateLinkService(service) {
        const selector = `link[data-pixelmate-href][data-consent-service="${service}"]`;
        document.querySelectorAll(selector).forEach(link => this.activateLink(link));
    }

    /**
     * Stellt ein einzelnes blockiertes <link>-Tag wieder her (href zurücksetzen).
     * Idempotent: nach der Wiederherstellung entfällt data-pixelmate-href, sodass
     * das Element von keinem Selector erneut erfasst wird.
     */
    activateLink(link) {
        const href = link.getAttribute('data-pixelmate-href');
        if (!href) return;
        link.setAttribute('href', href);
        link.removeAttribute('data-pixelmate-href');
        pmLog('  → Activated blocked link:', href);
    }

    /**
     * Stellt blockierte <img>-Tags einer Kategorie wieder her (z.B. VG-WORT-Zählmarke).
     * PHP (ServiceInjector::block_vg_wort_assets) ersetzt src durch data-pixelmate-src,
     * damit der Browser die Zählmarke ohne Consent nicht lädt.
     */
    activateImagesForCategory(category) {
        const selector = `img[data-pixelmate-src][data-consent="${category}"]`;
        document.querySelectorAll(selector).forEach(img => this.activateImage(img));
    }

    /**
     * Stellt blockierte <img>-Tags eines Services wieder her.
     */
    activateImagesForService(service) {
        const selector = `img[data-pixelmate-src][data-consent-service="${service}"]`;
        document.querySelectorAll(selector).forEach(img => this.activateImage(img));
    }

    /**
     * Stellt ein einzelnes blockiertes <img>-Tag wieder her (src zurücksetzen).
     * Idempotent: nach der Wiederherstellung entfällt data-pixelmate-src, sodass
     * das Element von keinem Selector erneut erfasst wird.
     */
    activateImage(img) {
        const src = img.getAttribute('data-pixelmate-src');
        if (!src) return;
        img.setAttribute('src', src);
        img.removeAttribute('data-pixelmate-src');
        pmLog('  → Activated blocked image:', src);
    }

    /**
     * iframes einer Kategorie aktivieren (nur nicht-service-spezifische)
     */
    activateIframes(category) {
        const selector = `iframe[data-consent="${category}"][data-src]:not([data-consent-service])`;
        const blockedIframes = document.querySelectorAll(selector);

        pmLog(`Found ${blockedIframes.length} blocked iframe(s) for category`);

        blockedIframes.forEach(iframe => {
            if (iframe.dataset.src) {
                const service = iframe.dataset.service || '';
                iframe.src = this.maybeConvertToNoCookieUrl(iframe.dataset.src, service);
                iframe.removeAttribute('data-src');
                // sandbox-Attribut entfernen, damit der Dienst voll funktionsfähig ist
                iframe.removeAttribute('sandbox');
                // Display wiederherstellen falls versteckt
                if (iframe.style.display === 'none') {
                    iframe.style.display = '';
                }
                pmLog('  → Activated category iframe:', iframe.src);
            }
        });
    }

    /**
     * Prüft, ob der Browser für das aktuelle Dokument jemals eine echte
     * Nutzerinteraktion (Klick, Tastendruck, Touch) registriert hat.
     *
     * Dient als Schutz gegen automatisiertes/programmatisches Erteilen von
     * Consent durch Skripte, die ohne Zutun des Besuchers laufen (z. B. ein
     * kompromittiertes Drittanbieter-Skript, das versucht, sich selbst über
     * updateConsent()/acceptAll() freizuschalten). Nutzt die standardisierte
     * User-Activation-API (https://html.spec.whatwg.org/multipage/interaction.html#tracking-user-activation),
     * dieselbe Grundlage, auf der Browser z. B. Autoplay- oder Popup-Schutz
     * aufbauen.
     *
     * In Browsern ohne diese API (kein Support) wird nicht blockiert, um
     * bestehendes Verhalten nicht zu beeinträchtigen.
     */
    _hasUserActivation() {
        if (typeof navigator === 'undefined' || !navigator.userActivation) {
            return true;
        }
        return navigator.userActivation.hasBeenActive === true;
    }

    /**
     * Consent aktualisieren und neue Kategorien/Services aktivieren
     */
    updateConsent(categories, services = null) {
        // Nur neu erteilte (nicht bereits vorhandene) Zustimmungen erfordern
        // eine echte Nutzerinteraktion. Rücknahmen (false) und No-Op-Updates
        // bleiben davon unberührt, damit z. B. der automatische GPC-Widerruf
        // (siehe PixelmateBanner.applyGpcConsent) weiterhin ohne Klick funktioniert.
        const grantsNewCategoryConsent = Object.keys(categories).some(
            category => categories[category] && category !== 'necessary' && !this.consent[category]
        );
        const grantsNewServiceConsent = !!services && Object.keys(services).some(
            service => services[service] && !this.serviceConsent[service]
        );

        if ((grantsNewCategoryConsent || grantsNewServiceConsent) && !this._hasUserActivation()) {
            pmWarn('Consent-Erteilung ohne erkennbare Nutzerinteraktion blockiert.', categories, services);
            return {
                categories: this.consent,
                services: this.serviceConsent
            };
        }

        pmLog('Updating consent:', categories, 'Services:', services);

        // Consent-Status aktualisieren
        Object.keys(categories).forEach(category => {
            const wasAccepted = this.consent[category];
            const isAccepted = categories[category];

            // Neu akzeptiert? → Aktivieren
            if (isAccepted && !wasAccepted) {
                this.consent[category] = true;
                this.activateCategory(category);
            } else if (!isAccepted) {
                this.consent[category] = false;
            }
        });

        // Service-spezifische Consents aktualisieren
        if (services) {
            Object.keys(services).forEach(service => {
                const wasAccepted = this.serviceConsent[service];
                const isAccepted = services[service];

                // Neu akzeptiert? → Aktivieren
                if (isAccepted && !wasAccepted) {
                    this.serviceConsent[service] = true;
                    this.activateService(service);
                } else if (!isAccepted) {
                    this.serviceConsent[service] = false;
                }
            });
        }

        // Update GTM Consent Mode after changing consent
        this.updateGTMConsent();

        // Publish state for third-party tools (always runs, independent of GTM)
        this._publishConsentState();

        return {
            categories: this.consent,
            services: this.serviceConsent
        };
    }

    /**
     * Alle Kategorien und Services akzeptieren
     */
    acceptAll() {
        // Alle verfügbaren Services sammeln
        const allServices = {};
        const serviceElements = document.querySelectorAll('[data-consent-service]');
        serviceElements.forEach(el => {
            const service = el.dataset.consentService;
            if (service) {
                allServices[service] = true;
            }
        });

        return this.updateConsent({
            necessary: true,
            analytics: true,
            marketing: true,
            external: true
        }, allServices);
    }

    /**
     * Alle nicht-notwendigen Kategorien und Services ablehnen
     */
    rejectAll() {
        // Alle verfügbaren Services sammeln und auf false setzen
        const allServices = {};
        const serviceElements = document.querySelectorAll('[data-consent-service]');
        serviceElements.forEach(el => {
            const service = el.dataset.consentService;
            if (service) {
                allServices[service] = false;
            }
        });

        return this.updateConsent({
            necessary: true,
            analytics: false,
            marketing: false,
            external: false
        }, allServices);
    }

    /**
     * Send Consent Mode v2 updates to Google Tag Manager (if configured)
     */
    updateGTMConsent() {
        // Check if GTM config is available
        if (!window.pixelmateGTMConfig) {
            return;
        }

        const gtmConfig = window.pixelmateGTMConfig;

        // Check if consent mode is enabled
        if (!gtmConfig.consentMode) {
            if (gtmConfig.debug) {
                console.log('[Pixelmate Debug] GTM Consent Mode not enabled');
            }
            return;
        }

        // Check if gtag function exists
        if (typeof window.gtag !== 'function') {
            if (gtmConfig.debug) {
                console.log('[Pixelmate Debug] gtag function not available yet');
            }
            return;
        }

        const consentParameters = gtmConfig.consentParameters || ['ad_storage', 'analytics_storage', 'ad_user_data', 'ad_personalization'];

        // Build consent update object based on Pixelmate categories
        const consentUpdate = {};

        consentParameters.forEach(param => {
            // Map GTM consent parameters to Pixelmate categories
            switch (param) {
                case 'ad_storage':
                case 'ad_user_data':
                case 'ad_personalization':
                    // Marketing category controls ad-related consent
                    consentUpdate[param] = this.consent.marketing ? 'granted' : 'denied';
                    break;

                case 'analytics_storage':
                    // Analytics category controls analytics consent
                    consentUpdate[param] = this.consent.analytics ? 'granted' : 'denied';
                    break;

                case 'functionality_storage':
                case 'personalization_storage':
                    // Functional/Necessary category controls functionality consent
                    consentUpdate[param] = (this.consent.necessary || this.consent.external) ? 'granted' : 'denied';
                    break;

                default:
                    consentUpdate[param] = 'denied';
            }
        });

        // Send consent update to GTM
        window.gtag('consent', 'update', consentUpdate);

        if (gtmConfig.debug) {
            console.log('[Pixelmate Debug] GTM Consent Mode v2 updated:', consentUpdate);
        }

        // Also push to dataLayer for custom tracking
        if (window.dataLayer) {
            window.dataLayer.push({
                'event': 'pixelmate_consent_update',
                'pixelmate_consent': {
                    categories: this.consent,
                    services: this.serviceConsent,
                    timestamp: Date.now()
                }
            });

            if (gtmConfig.debug) {
                console.log('[Pixelmate Debug] Custom consent event pushed to dataLayer');
            }
        }
    }

    /**
     * Exposes the current consent state globally and dispatches a DOM event.
     * Allows third-party tools and plugins to read or react to consent changes
     * without requiring GTM or IAB TCF.
     *
     * window.pixelmateConsent wird bewusst als nicht-überschreibbares,
     * eingefrorenes Objekt veröffentlicht: Andere Skripte auf der Seite sollen
     * den Consent-Zustand lesen, aber nicht durch einfaches Überschreiben von
     * window.pixelmateConsent vortäuschen können. Die eigentliche Erteilung
     * bleibt ausschließlich über updateConsent()/acceptAll()/rejectAll()
     * möglich (dort durch _hasUserActivation() geschützt).
     */
    _publishConsentState() {
        const state = Object.freeze({
            categories: Object.freeze({ ...this.consent }),
            services: Object.freeze({ ...this.serviceConsent }),
            timestamp: Date.now(),
            hasConsent: (category) => !!this.consent[category]
        });

        try {
            Object.defineProperty(window, 'pixelmateConsent', {
                value: state,
                writable: false,
                configurable: true,
                enumerable: true
            });
        } catch (e) {
            window.pixelmateConsent = state;
        }

        document.dispatchEvent(new CustomEvent('pixelmate:consent', {
            bubbles: true,
            detail: state
        }));
    }
}

